import { graphql } from '@/lib/graphql';
import { useMutation } from '@/lib/graphql/hooks';
import { BookmarkIcon } from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkSolidIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

// --- GraphQL Mutation 定義 ---
const CREATE_BOOKMARK_MUTATION = graphql(`
  mutation createBookmark($input: CreateArticleBookmarkInput!) {
    createArticleBookmark(input: $input) {
      success
      userErrors {
        message
        code
      }
      articleBookmark {
        id
      }
    }
  }
`);

const DELETE_BOOKMARK_MUTATION = graphql(`
  mutation deleteBookmark($input: DeleteArticleBookmarkInput!) {
    deleteArticleBookmark(input: $input) {
      success
      userErrors {
        message
        code
      }
    }
  }
`);
// --- ---

interface BookmarkButtonProps {
  code: string;
  title: string;
  url: string;
  isBookmarked: boolean;
  className?: string;
}

export const BookmarkButton = ({
  code,
  title,
  url,
  isBookmarked,
  className,
}: BookmarkButtonProps) => {
  const { t } = useTranslation();
  const [createBookmark, { loading: creating }] = useMutation(
    CREATE_BOOKMARK_MUTATION
    // TODO: 手動キャッシュ更新を削除し、Apollo Clientの自動更新に任せる
    // apollo-cache-patterns.md のルールに従って修正が必要
  );
  const [deleteBookmark, { loading: deleting }] = useMutation(
    DELETE_BOOKMARK_MUTATION
    // TODO: 手動キャッシュ更新を削除し、Apollo Clientの自動更新に任せる
    // apollo-cache-patterns.md のルールに従って修正が必要
  );
  const isLoading = creating || deleting;

  const handleToggle = async () => {
    if (isBookmarked) {
      await deleteBookmark({
        variables: { input: { articleId: code } },
        update: (cache) => {
          cache.modify({
            id: cache.identify({ __typename: 'ActiveArticle', code }),
            fields: { isBookmarkedByCurrentUser: () => false },
          });
        },
      });
    } else {
      await createBookmark({
        variables: {
          input: {
            articleId: code,
            backupArticleTitle: title,
            backupArticleUrl: url,
          },
        },
        update: (cache) => {
          cache.modify({
            id: cache.identify({ __typename: 'ActiveArticle', code }),
            fields: { isBookmarkedByCurrentUser: () => true },
          });
        },
      });
    }
  };

  return (
    <button
      type="button"
      onClick={handleToggle}
      disabled={isLoading}
      aria-label={isBookmarked ? t('ブックマーク済み') : t('ブックマークする')}
      className={clsx(
        'grid size-7 place-items-center rounded-full',
        isBookmarked ? 'bg-rw-dark-gray' : 'bg-rw-white',
        className
      )}
    >
      {isBookmarked ? (
        <BookmarkSolidIcon className="size-4 stroke-2 text-rw-white" />
      ) : (
        <BookmarkIcon className="size-4 stroke-2" />
      )}
    </button>
  );
};
